/**
 * Admin UI draft for customizing 雇入通知書兼雇用契約書 at invitation time.
 */
import React from 'react';
import {
  defaultEmploymentContractAdminValues,
  type AmPm,
  type EmploymentContractAdminValues,
  type ScheduleSlot,
  type WorkScheduleRow,
  type YesNo,
} from './employmentContractAdminTypes';
import {
  EMP_CONTRACT_TEXT_LIMITS,
  sanitizeBreakMinutes,
  sanitizeCompensationDays,
  sanitizeDailyWorkHours,
  sanitizeHour12,
  sanitizeMinute,
  sanitizeOvertimeHours,
  sanitizePercentRate,
  sanitizePersonName,
  sanitizeText,
  sanitizeYenAmount,
} from './employmentContractInputLimits';
import { formatIsoAsReiwaDisplay } from './reiwaDateDisplay';
import './EmploymentContractAdminForm.css';

export interface EmploymentContractAdminFormProps {
  value: EmploymentContractAdminValues;
  onChange: (next: EmploymentContractAdminValues) => void;
}

function ReiwaDateField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (iso: string) => void;
}) {
  return (
    <div className="emp-contract-form__reiwa-date">
      <span className="emp-contract-form__reiwa-display">
        {formatIsoAsReiwaDisplay(value)}
      </span>
      <input
        id={id}
        type="date"
        className="emp-contract-form__date-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={value ? formatIsoAsReiwaDisplay(value) : '日付を選択'}
      />
    </div>
  );
}

function YesNoRadios({
  name,
  value,
  onChange,
}: {
  name: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
}) {
  return (
    <span className="emp-contract-form__inline-radios">
      <label className="emp-contract-form__radio">
        <input
          type="radio"
          name={name}
          checked={value === 'yes'}
          onChange={() => onChange('yes')}
        />
        有
      </label>
      <span className="emp-contract-form__dot" aria-hidden>
        ・
      </span>
      <label className="emp-contract-form__radio">
        <input
          type="radio"
          name={name}
          checked={value === 'no'}
          onChange={() => onChange('no')}
        />
        無
      </label>
    </span>
  );
}

function AmPmSelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: AmPm;
  onChange: (v: AmPm) => void;
  disabled?: boolean;
}) {
  return (
    <select
      id={id}
      className="emp-contract-form__select emp-contract-form__select--narrow"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as AmPm)}
    >
      <option value="am">午前</option>
      <option value="pm">午後</option>
    </select>
  );
}

function ScheduleRowEditor({
  slot,
  enabled,
  onEnabledChange,
  row,
  onRowChange,
}: {
  slot: ScheduleSlot;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  row: WorkScheduleRow;
  onRowChange: (next: WorkScheduleRow) => void;
}) {
  const set = <K extends keyof WorkScheduleRow>(key: K, val: WorkScheduleRow[K]) =>
    onRowChange({ ...row, [key]: val });

  return (
    <div
      className={`emp-contract-form__schedule-row${
        enabled ? '' : ' emp-contract-form__schedule-row--disabled'
      }`}
    >
      <label className="emp-contract-form__schedule-pick">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        <span>{slot}.</span>
      </label>
      <div className="emp-contract-form__schedule-fields">
        <div className="emp-contract-form__time-group">
          <span className="emp-contract-form__time-label">始業</span>
          <input
            className="emp-contract-form__input emp-contract-form__input--num"
            inputMode="numeric"
            maxLength={2}
            value={row.startHour}
            onChange={(e) => set('startHour', sanitizeHour12(e.target.value))}
            disabled={!enabled}
            aria-label={`始業${slot}時`}
          />
          <span>時</span>
          <input
            className="emp-contract-form__input emp-contract-form__input--num"
            inputMode="numeric"
            maxLength={2}
            value={row.startMinute}
            onChange={(e) => set('startMinute', sanitizeMinute(e.target.value))}
            disabled={!enabled}
            aria-label={`始業${slot}分`}
          />
          <span>分</span>
          <AmPmSelect
            id={`sched-${slot}-start-ampm`}
            value={row.startAmPm}
            onChange={(v) => set('startAmPm', v)}
            disabled={!enabled}
          />
        </div>
        <div className="emp-contract-form__time-group">
          <span className="emp-contract-form__time-label">終業</span>
          <input
            className="emp-contract-form__input emp-contract-form__input--num"
            inputMode="numeric"
            maxLength={2}
            value={row.endHour}
            onChange={(e) => set('endHour', sanitizeHour12(e.target.value))}
            disabled={!enabled}
            aria-label={`終業${slot}時`}
          />
          <span>時</span>
          <input
            className="emp-contract-form__input emp-contract-form__input--num"
            inputMode="numeric"
            maxLength={2}
            value={row.endMinute}
            onChange={(e) => set('endMinute', sanitizeMinute(e.target.value))}
            disabled={!enabled}
            aria-label={`終業${slot}分`}
          />
          <span>分</span>
          <AmPmSelect
            id={`sched-${slot}-end-ampm`}
            value={row.endAmPm}
            onChange={(v) => set('endAmPm', v)}
            disabled={!enabled}
          />
        </div>
        <div className="emp-contract-form__time-group">
          <span className="emp-contract-form__time-label">うち休憩時間</span>
          <input
            className="emp-contract-form__input emp-contract-form__input--num"
            inputMode="numeric"
            maxLength={3}
            value={row.breakMinutes}
            onChange={(e) => set('breakMinutes', sanitizeBreakMinutes(e.target.value))}
            disabled={!enabled}
            aria-label={`休憩${slot}`}
          />
          <span>分</span>
        </div>
      </div>
    </div>
  );
}

const EmploymentContractAdminForm: React.FC<EmploymentContractAdminFormProps> = ({
  value,
  onChange,
}) => {
  const patch = (partial: Partial<EmploymentContractAdminValues>) =>
    onChange({ ...value, ...partial });

  return (
    <div className="emp-contract-form">
      <div className="emp-contract-form__toolbar">
        <h3 className="emp-contract-form__heading">雇入通知</h3>
        <button
          type="button"
          className="emp-contract-form__reset-btn"
          onClick={() => onChange(defaultEmploymentContractAdminValues())}
        >
          初期設定に戻す
        </button>
      </div>

      {/* 1. 雇用期間 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">1. 雇用期間</div>
        <div className="emp-contract-form__section-body">
          <label className="emp-contract-form__radio emp-contract-form__radio--block">
            <input
              type="radio"
              name="employmentTermType"
              checked={value.employmentTermType === 'indefinite'}
              onChange={() => patch({ employmentTermType: 'indefinite' })}
            />
            1. 期間の定めなし
          </label>
          <div className="emp-contract-form__term-fixed">
            <label className="emp-contract-form__radio">
              <input
                type="radio"
                name="employmentTermType"
                checked={value.employmentTermType === 'fixed'}
                onChange={() => patch({ employmentTermType: 'fixed' })}
              />
              2. 期間の定めあり
            </label>
            <span className="emp-contract-form__paren">(</span>
            <ReiwaDateField
              id="term-start"
              value={value.termStartDate}
              onChange={(termStartDate) => patch({ termStartDate, employmentTermType: 'fixed' })}
            />
            <span className="emp-contract-form__tilde">〜</span>
            <ReiwaDateField
              id="term-end"
              value={value.termEndDate}
              onChange={(termEndDate) => patch({ termEndDate, employmentTermType: 'fixed' })}
            />
            <span className="emp-contract-form__paren">)</span>
          </div>
          <div className="emp-contract-form__field-row">
            <span className="emp-contract-form__field-caption">上記の場合の理由</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--grow"
              value={value.termReason}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.termReason}
              onChange={(e) =>
                patch({
                  termReason: sanitizeText(e.target.value, EMP_CONTRACT_TEXT_LIMITS.termReason),
                })
              }
              disabled={value.employmentTermType !== 'fixed'}
            />
          </div>
        </div>
      </section>

      {/* 2. 就業場所 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">2. 就業場所</div>
        <div className="emp-contract-form__section-body emp-contract-form__section-body--place">
          <div className="emp-contract-form__place-line">
            <input
              className="emp-contract-form__input"
              value={value.workplacePrefectureName}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.workplacePrefectureName}
              onChange={(e) =>
                patch({
                  workplacePrefectureName: sanitizeText(
                    e.target.value,
                    EMP_CONTRACT_TEXT_LIMITS.workplacePrefectureName
                  ),
                })
              }
              aria-label="都道府県名"
            />
            <select
              className="emp-contract-form__select emp-contract-form__select--unit"
              value={value.workplacePrefectureSuffix}
              onChange={(e) =>
                patch({
                  workplacePrefectureSuffix: e.target.value as EmploymentContractAdminValues['workplacePrefectureSuffix'],
                })
              }
              aria-label="都・道・府・県"
            >
              <option value="都">都</option>
              <option value="道">道</option>
              <option value="府">府</option>
              <option value="県">県</option>
            </select>
          </div>
          <div className="emp-contract-form__place-line">
            <input
              className="emp-contract-form__input"
              value={value.workplaceCityName}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.workplaceCityName}
              onChange={(e) =>
                patch({
                  workplaceCityName: sanitizeText(
                    e.target.value,
                    EMP_CONTRACT_TEXT_LIMITS.workplaceCityName
                  ),
                })
              }
              aria-label="区市町村名"
            />
            <select
              className="emp-contract-form__select emp-contract-form__select--unit"
              value={value.workplaceCitySuffix}
              onChange={(e) =>
                patch({
                  workplaceCitySuffix: e.target.value as EmploymentContractAdminValues['workplaceCitySuffix'],
                })
              }
              aria-label="区・市・郡"
            >
              <option value="区">区</option>
              <option value="市">市</option>
              <option value="郡">郡</option>
            </select>
          </div>
          <div className="emp-contract-form__place-line">
            <input
              className="emp-contract-form__input emp-contract-form__input--grow"
              value={value.workplaceSite}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.workplaceSite}
              onChange={(e) =>
                patch({
                  workplaceSite: sanitizeText(
                    e.target.value,
                    EMP_CONTRACT_TEXT_LIMITS.workplaceSite
                  ),
                })
              }
              aria-label="地区内の作業場"
            />
            <span className="emp-contract-form__place-suffix">地区内の作業場</span>
          </div>
        </div>
      </section>

      {/* 3. 業務内容 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">3. 業務内容</div>
        <div className="emp-contract-form__section-body">
          <textarea
            className="emp-contract-form__textarea"
            rows={3}
            value={value.jobDescription}
            maxLength={EMP_CONTRACT_TEXT_LIMITS.jobDescription}
            onChange={(e) =>
              patch({
                jobDescription: sanitizeText(
                  e.target.value,
                  EMP_CONTRACT_TEXT_LIMITS.jobDescription
                ),
              })
            }
          />
        </div>
      </section>

      {/* 4. 所定就業時間 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">4. 所定就業時間</div>
        <div className="emp-contract-form__section-body">
          <div className="emp-contract-form__flags">
            <div className="emp-contract-form__flag">
              <span>○交替制</span>
              <YesNoRadios
                name="shiftSystem"
                value={value.shiftSystem}
                onChange={(shiftSystem) => patch({ shiftSystem })}
              />
            </div>
            <div className="emp-contract-form__flag">
              <span>○所定時間外労働</span>
              <YesNoRadios
                name="overtimeWork"
                value={value.overtimeWork}
                onChange={(overtimeWork) => patch({ overtimeWork })}
              />
            </div>
          </div>
          <ScheduleRowEditor
            slot="1"
            enabled={value.schedule1Enabled}
            onEnabledChange={(schedule1Enabled) => patch({ schedule1Enabled })}
            row={value.schedule1}
            onRowChange={(schedule1) => patch({ schedule1 })}
          />
          <ScheduleRowEditor
            slot="2"
            enabled={value.schedule2Enabled}
            onEnabledChange={(schedule2Enabled) => patch({ schedule2Enabled })}
            row={value.schedule2}
            onRowChange={(schedule2) => patch({ schedule2 })}
          />
          <div className="emp-contract-form__field-row">
            <span>・実働1日（</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--num"
              inputMode="decimal"
              value={value.dailyWorkHours}
              onChange={(e) => patch({ dailyWorkHours: sanitizeDailyWorkHours(e.target.value) })}
            />
            <span>時間）</span>
          </div>
        </div>
      </section>

      {/* 5. 賃金 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">5. 賃金</div>
        <div className="emp-contract-form__section-body">
          <div className="emp-contract-form__field-row">
            <span>・基本賃金　基本給</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--money"
              inputMode="numeric"
              maxLength={9}
              value={value.baseSalary}
              onChange={(e) => patch({ baseSalary: sanitizeYenAmount(e.target.value) })}
              aria-label="基本給"
            />
            <span>円（補償給</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--num"
              inputMode="numeric"
              maxLength={2}
              value={value.compensationPerDay}
              onChange={(e) =>
                patch({ compensationPerDay: sanitizeCompensationDays(e.target.value) })
              }
              aria-label="補償給（日に付き）"
            />
            <span>日に付き）</span>
          </div>

          <div className="emp-contract-form__rates">
            <div className="emp-contract-form__rates-title">・割増賃金率</div>
            <div className="emp-contract-form__rate-grid">
              <span className="emp-contract-form__rate-label">所定時間外</span>
              <div className="emp-contract-form__rate-pair">
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={2}
                  value={value.overtimeWithinHours}
                  onChange={(e) =>
                    patch({ overtimeWithinHours: sanitizeOvertimeHours(e.target.value) })
                  }
                />
                <span>h以内</span>
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={3}
                  value={value.overtimeWithinRate}
                  onChange={(e) =>
                    patch({ overtimeWithinRate: sanitizePercentRate(e.target.value) })
                  }
                />
                <span>%</span>
              </div>
              <span />
              <div className="emp-contract-form__rate-pair">
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={2}
                  value={value.overtimeOverHours}
                  onChange={(e) =>
                    patch({ overtimeOverHours: sanitizeOvertimeHours(e.target.value) })
                  }
                />
                <span>h超</span>
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={3}
                  value={value.overtimeOverRate}
                  onChange={(e) =>
                    patch({ overtimeOverRate: sanitizePercentRate(e.target.value) })
                  }
                />
                <span>%</span>
              </div>

              <span className="emp-contract-form__rate-label">法定休日</span>
              <div className="emp-contract-form__rate-pair">
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={3}
                  value={value.statutoryHolidayRate}
                  onChange={(e) =>
                    patch({ statutoryHolidayRate: sanitizePercentRate(e.target.value) })
                  }
                />
                <span>%</span>
              </div>

              <span className="emp-contract-form__rate-label">法定外休日</span>
              <div className="emp-contract-form__rate-pair">
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={3}
                  value={value.nonStatutoryHolidayRate}
                  onChange={(e) =>
                    patch({ nonStatutoryHolidayRate: sanitizePercentRate(e.target.value) })
                  }
                />
                <span>%</span>
              </div>

              <span className="emp-contract-form__rate-label">深夜</span>
              <div className="emp-contract-form__rate-pair">
                <input
                  className="emp-contract-form__input emp-contract-form__input--num"
                  inputMode="numeric"
                  maxLength={3}
                  value={value.nightRate}
                  onChange={(e) => patch({ nightRate: sanitizePercentRate(e.target.value) })}
                />
                <span>%</span>
              </div>
            </div>
          </div>

          <div className="emp-contract-form__field-row">
            <span>・退職金制度</span>
            <select
              className="emp-contract-form__select"
              value={value.retirementPlan}
              onChange={(e) =>
                patch({
                  retirementPlan: e.target.value as EmploymentContractAdminValues['retirementPlan'],
                })
              }
            >
              <option value="chutaikyo">中退共</option>
              <option value="kentaikyo">建退共</option>
              <option value="none">無</option>
            </select>
          </div>

          <div className="emp-contract-form__field-row emp-contract-form__field-row--wrap">
            <span>・諸手当の名称、金額、計算方法</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--grow"
              value={value.allowances}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.allowances}
              onChange={(e) =>
                patch({
                  allowances: sanitizeText(e.target.value, EMP_CONTRACT_TEXT_LIMITS.allowances),
                })
              }
            />
          </div>
        </div>
      </section>

      {/* 6. その他 */}
      <section className="emp-contract-form__section">
        <div className="emp-contract-form__section-label">6. その他</div>
        <div className="emp-contract-form__section-body">
          <div className="emp-contract-form__field-row emp-contract-form__field-row--wrap">
            <span>1. 社会保険の加入状況（</span>
            <label className="emp-contract-form__radio">
              <input
                type="checkbox"
                checked={value.socialInsuranceHealth}
                onChange={(e) => patch({ socialInsuranceHealth: e.target.checked })}
              />
              健康保険
            </label>
            <span className="emp-contract-form__dot" aria-hidden>
              ・
            </span>
            <label className="emp-contract-form__radio">
              <input
                type="checkbox"
                checked={value.socialInsurancePension}
                onChange={(e) => patch({ socialInsurancePension: e.target.checked })}
              />
              厚生年金
            </label>
            <span>）</span>
          </div>
          <div className="emp-contract-form__field-row">
            <span>2. 雇用保険の適用（</span>
            <YesNoRadios
              name="employmentInsurance"
              value={value.employmentInsurance}
              onChange={(employmentInsurance) => patch({ employmentInsurance })}
            />
            <span>）</span>
          </div>
        </div>
      </section>

      {/* 雇用契約書 */}
      <section className="emp-contract-form__section emp-contract-form__section--contract">
        <div className="emp-contract-form__section-label emp-contract-form__section-label--tall">
          雇用
          <br />
          契約書
        </div>
        <div className="emp-contract-form__section-body">
          <div className="emp-contract-form__field-row">
            <span>使用者　所長</span>
            <input
              className="emp-contract-form__input emp-contract-form__input--grow"
              value={value.siteManagerName}
              maxLength={EMP_CONTRACT_TEXT_LIMITS.siteManagerName}
              onChange={(e) =>
                patch({
                  siteManagerName: sanitizePersonName(
                    e.target.value,
                    EMP_CONTRACT_TEXT_LIMITS.siteManagerName
                  ),
                })
              }
            />
          </div>
          <div className="emp-contract-form__contract-date">
            <ReiwaDateField
              id="contract-date"
              value={value.contractDate}
              onChange={(contractDate) => patch({ contractDate })}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmploymentContractAdminForm;
